import { supabase } from "@/lib/supabase";
const T = {
  articles: "article",
  blogs: "blog",
  news: "news",
  pages: "page",
  events: "event",
  opportunities: "opportunity",
  initiatives: "initiative",
  organizations: "organization",
};
const H = {
  communities: "heritage_communities",
  places: "heritage_places",
  sources: "research_sources",
};
const ok = (data) => ({ data });
function bad(e, status = 400) {
  const x = new Error(e?.message || String(e));
  x.response = { status, data: { detail: x.message } };
  throw x;
}
async function run(p) {
  const { data, error } = await p;
  if (error) bad(error);
  return data;
}
const flat = (x) =>
  x
    ? {
        ...x.metadata,
        ...x,
        category: x.categories?.name || x.category || "",
        author: x.cms_authors?.name || x.metadata?.author || "",
      }
    : x;
async function me() {
  const { data, error } = await supabase.auth.getUser();
  if (error || !data.user) bad(error || "Not authenticated", 401);
  const u = data.user,
    [p, r] = await Promise.all([
      run(supabase.from("profiles").select("*").eq("id", u.id).maybeSingle()),
      run(
        supabase
          .from("user_roles")
          .select("*")
          .eq("user_id", u.id)
          .maybeSingle(),
      ),
    ]);
  return {
    id: u.id,
    email: u.email,
    email_verified: !!u.email_confirmed_at,
    ...p,
    avatar: p?.avatar_url,
    organization: p?.organisation,
    role: r?.role || "member",
    can_publish: !!r?.can_publish,
  };
}
async function authUser() {
  const { data, error } = await supabase.auth.getUser();
  if (error || !data.user) bad(error || "Not authenticated", 401);
  return data.user;
}
function profileRow(b) {
  const allowed = [
      "first_name",
      "last_name",
      "display_name",
      "avatar_url",
      "bio",
      "location",
      "organisation",
      "profession",
      "country",
      "country_code",
      "city",
      "area_of_interest",
      "linkedin",
      "website",
      "skills",
      "interests",
      "privacy",
      "job_title",
      "heritage_community_id",
      "heritage_branch",
      "heritage_subbranch",
      "heritage_visibility",
      "profile_visibility",
      "contact_visibility",
      "email_preferences",
      "notification_preferences",
      "last_active_at",
    ],
    x = {};
  allowed.forEach((k) => {
    if (b[k] !== undefined) x[k] = b[k];
  });
  if (b.organization !== undefined) x.organisation = b.organization;
  if (b.linkedin_url !== undefined) x.linkedin = b.linkedin_url;
  if (b.website_url !== undefined) x.website = b.website_url;
  x.display_name =
    b.display_name || `${b.first_name || ""} ${b.last_name || ""}`.trim();
  return x;
}
async function list(resource, staff = false, params = {}) {
  let q = supabase
    .from("content_items")
    .select("*,categories(name),cms_authors(name)")
    .eq("content_type", T[resource])
    .is("deleted_at", null)
    .order("updated_at", { ascending: false });
  if (!staff) q = q.eq("status", "published").eq("visibility", "public");
  if (params.status) q = q.eq("status", params.status);
  if (params.category && params.category !== "All")
    q = q.eq("category", params.category);
  let rows = (await run(q.limit(Number(params.limit) || 500))).map(flat),
    n = (params.q || "").toLowerCase();
  return n
    ? rows.filter((x) => JSON.stringify(x).toLowerCase().includes(n))
    : rows;
}
function row(resource, b) {
  const cols = [
      "title",
      "headline",
      "slug",
      "excerpt",
      "body",
      "description",
      "status",
      "visibility",
      "category",
      "region",
      "country",
      "tags",
      "blocks",
      "featured",
      "publish_at",
      "published_at",
      "starts_at",
      "ends_at",
      "primary_category_id",
      "cms_author_id",
      "read_time_override",
      "allow_comments",
      "allow_likes",
      "show_view_count",
    ],
    r = { content_type: T[resource] };
  cols.forEach((k) => {
    if (b[k] !== undefined) r[k] = b[k];
  });
  r.title = b.title || b.headline || b.name || "Untitled";
  r.slug =
    b.slug ||
    r.title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");
  const s = new Set(
    cols.concat(["id", "metadata", "created_at", "updated_at"]),
  );
  r.metadata = {
    ...(b.metadata || {}),
    ...Object.fromEntries(Object.entries(b).filter(([k]) => !s.has(k))),
  };
  return r;
}
async function site() {
  const [b, n, f, s, h, builder, identity] = await Promise.all([
    run(supabase.from("branding_settings").select("*").limit(1).maybeSingle()).catch(() => null),
    run(supabase.from("navigation_items").select("*").order("position")).catch(() => []),
    run(supabase.from("footer_settings").select("*").limit(1).maybeSingle()).catch(() => null),
    run(supabase.from("seo_settings").select("*").limit(1).maybeSingle()).catch(() => null),
    run(supabase.from("homepage_sections").select("*").order("position")).catch(() => []),
    run(supabase.rpc("get_public_builder_config")).catch(() => ({})),
    run(supabase.rpc("get_public_identity_config")).catch(() => ({})),
  ]);
  return {
    branding: flat(b) || {},
    navigation: { id: "primary", items: n },
    footer: flat(f) || {},
    seo: flat(s) || {},
    homepage: {
      id: "homepage",
      sections: h.map((x) => ({
        ...x.settings,
        id: x.section_key,
        type: x.section_type,
        title: x.title,
        visible: x.visible,
      })),
    },
    builder: builder || {},
    identity: identity || {},
  };
}
async function get(path, c = {}) {
  let m,
    p = c.params || {};
  if (path === "/auth/me") return ok(await me());
  if (path === "/site-config") return ok(await site());
  if (path === "/heritage") {
    const [communities, places] = await Promise.all([
      run(
        supabase
          .from(H.communities)
          .select("*")
          .eq("review_status", "published")
          .order("name"),
      ),
      run(
        supabase
          .from(H.places)
          .select("*")
          .eq("review_status", "published")
          .order("name"),
      ),
    ]);
    return ok({ communities, places });
  }
  if (path === "/heritage/sources")
    return ok(
      await run(supabase.from(H.sources).select("*").eq("status", "published")),
    );
  if ((m = path.match(/^\/heritage\/places\/(.+)$/)))
    return ok(
      await run(supabase.from(H.places).select("*").eq("slug", m[1]).single()),
    );
  if ((m = path.match(/^\/heritage\/(.+)$/)))
    return ok(
      await run(
        supabase.from(H.communities).select("*").eq("slug", m[1]).single(),
      ),
    );
  if (path === "/locations") {
    let x = await run(
      supabase.from(H.places).select("*").eq("review_status", "published"),
    );
    return ok(
      x.map((v) => ({
        ...v,
        lat: v.latitude,
        lng: v.longitude,
        role: v.place_type,
        status: v.place_type,
        description: v.summary,
        community: v.community_presence,
        population: v.population_estimate_label || v.presence_label,
      })),
    );
  }
  if ((m = path.match(/^\/locations\/(.+)$/)))
    return ok(
      await run(supabase.from(H.places).select("*").eq("slug", m[1]).single()),
    );
  if (path === "/network-regions")
    return ok(
      await run(
        supabase
          .from("network_regions")
          .select("*")
          .eq("review_status", "published")
          .order("sort_order"),
      ),
    );
  if (path === "/connections")
    return ok(
      (await run(supabase.from("map_connections").select("*"))).map((x) => ({
        ...x,
        ...x.metadata,
        source: x.source_legacy_id,
        target: x.target_legacy_id,
        type: x.connection_type,
      })),
    );
  if (path === "/global-network/summary") {
    const x = await run(
      supabase
        .from(H.places)
        .select("country,continent,place_type,estimate_status")
        .eq("review_status", "published"),
    );
    return ok({
      countries: new Set(x.map((v) => v.country).filter(Boolean)).size,
      continents: new Set(x.map((v) => v.continent).filter(Boolean)).size,
      nodes: x.length,
      estimates: x.filter((v) => v.estimate_status === "published").length,
      historical_sites: x.filter((v) => v.place_type === "historic_site")
        .length,
      diaspora_hubs: x.filter((v) => (v.place_type || "").includes("hub"))
        .length,
    });
  }
  if (path === "/taxonomy/categories")
    return ok(
      await run(
        supabase
          .from("categories")
          .select("*")
          .eq("status", "active")
          .order("sort_order"),
      ),
    );
  if (path === "/taxonomy/authors")
    return ok(
      await run(
        supabase
          .from("cms_authors")
          .select("*")
          .eq("status", "active")
          .order("sort_order"),
      ),
    );
  if (path.startsWith("/location/"))
    return ok(
      path.includes("countries")
        ? [
            "Somaliland",
            "Somalia",
            "Djibouti",
            "Ethiopia",
            "Kenya",
            "United Kingdom",
            "United States",
            "Canada",
          ].map((name) => ({ name, code: name === "Somaliland" ? "SL" : "" }))
        : [],
    );
  if (path.startsWith("/search")) {
    const n = (
        new URLSearchParams(path.split("?")[1] || "").get("q") || ""
      ).toLowerCase(),
      results = [];
    for (const r of Object.keys(T)) {
      (await list(r))
        .filter((x) => JSON.stringify(x).toLowerCase().includes(n))
        .slice(0, 5)
        .forEach((x) =>
          results.push({
            type: r,
            title: x.title,
            slug: x.slug,
            url: "/" + r + "/" + x.slug,
            excerpt: x.excerpt,
          }),
        );
    }
    const [communities, places] = await Promise.all([
      run(
        supabase
          .from(H.communities)
          .select("name,slug,summary,alternate_names,geography")
          .eq("review_status", "published"),
      ),
      run(
        supabase
          .from(H.places)
          .select("name,slug,summary,country,region,community_presence")
          .eq("review_status", "published"),
      ),
    ]);
    communities
      .filter((x) => JSON.stringify(x).toLowerCase().includes(n))
      .slice(0, 5)
      .forEach((x) =>
        results.push({
          type: "heritage",
          title: x.name,
          slug: x.slug,
          url: "/heritage/" + x.slug,
          excerpt: x.summary,
        }),
      );
    places
      .filter((x) => JSON.stringify(x).toLowerCase().includes(n))
      .slice(0, 5)
      .forEach((x) =>
        results.push({
          type: "location",
          title: x.name,
          slug: x.slug,
          url: "/global-network/" + x.slug,
          excerpt: x.summary,
        }),
      );
    return ok({ results: results.slice(0, 30) });
  }
  if ((m = path.match(/^\/cms\/heritage\/(communities|places|sources)$/)))
    return ok(
      await run(
        supabase
          .from(H[m[1]])
          .select("*")
          .order("updated_at", { ascending: false }),
      ),
    );
  if (path === "/cms/activity")
    return ok(
      await run(
        supabase
          .from("activity_logs")
          .select("*")
          .order("created_at", { ascending: false }),
      ),
    );
  if (path === "/cms/engagement/settings")
    return ok(
      await run(supabase.from("engagement_settings").select("*").single()),
    );
  if (path === "/cms/engagement/comments")
    return ok(
      await run(
        supabase
          .from("content_comments")
          .select("*")
          .order("created_at", { ascending: false }),
      ),
    );
  if (path === "/cms/engagement/summary") {
    const x = await run(
      supabase
        .from("content_items")
        .select(
          "id,title,slug,content_type,view_count,like_count,comment_count",
        )
        .in("content_type", ["article", "blog", "news"]),
    );
    return ok({
      totals: {
        views: x.reduce((a, v) => a + v.view_count, 0),
        likes: x.reduce((a, v) => a + v.like_count, 0),
        comments: x.reduce((a, v) => a + v.comment_count, 0),
      },
      top_content: x.sort((a, b) => b.view_count - a.view_count).slice(0, 10),
      views_by_day: [],
    });
  }
  if ((m = path.match(/^\/engagement\/([0-9a-f-]+)$/)))
    return ok(
      await run(supabase.rpc("get_content_engagement", { p_content_id: m[1] })),
    );
  if (path === "/me/saved") {
    const u = await authUser();
    return ok(
      await run(
        supabase
          .from("saved_content")
          .select("*")
          .eq("user_id", u.id)
          .order("created_at", { ascending: false }),
      ),
    );
  }
  if (path === "/me/platform-summary")
    return ok(await run(supabase.rpc("member_platform_summary")));
  if (path === "/me/notifications") {
    const u = await authUser();
    return ok(
      await run(
        supabase
          .from("notifications")
          .select("*")
          .eq("user_id", u.id)
          .order("created_at", { ascending: false })
          .limit(100),
      ),
    );
  }
  if (path === "/members") {
    const x = await run(
      supabase.rpc("search_member_directory", {
        p_query: p.q || "",
        p_country: p.country || "",
        p_city: p.city || "",
        p_limit: Number(p.limit) || 24,
        p_offset: Number(p.offset) || 0,
      }),
    );
    return ok(x.map((v) => ({ ...v, organization: v.organisation })));
  }
  if (path === "/me/connections") {
    await authUser();
    return ok(await run(supabase.rpc("get_my_connections")));
  }
  if (path === "/admin/newsletter")
    return ok(
      await run(
        supabase
          .from("newsletter_subscribers")
          .select("*")
          .order("created_at", { ascending: false }),
      ),
    );
  if (path === "/admin/contact-messages")
    return ok(
      await run(
        supabase
          .from("contact_messages")
          .select("*")
          .order("created_at", { ascending: false }),
      ),
    );
  if (path === "/site-settings") {
    const x = await run(supabase.rpc("get_public_site_settings"));
    return ok(x || {});
  }
  if (path === "/site-builder/config")
    return ok((await run(supabase.rpc("get_public_builder_config"))) || {});
  if (path === "/cms/site-builder/settings") {
    const rows = await run(
      supabase
        .from("site_settings")
        .select("*")
        .like("setting_key", "builder_%"),
    );
    return ok(
      Object.fromEntries(
        rows.map((x) => [x.setting_key.replace("builder_", ""), x.value]),
      ),
    );
  }
  if (path === "/cms/site-builder/revisions")
    return ok(
      await run(
        supabase
          .from("content_revisions")
          .select("*,profiles(display_name,email)")
          .order("created_at", { ascending: false })
          .limit(100),
      ),
    );
  if (path === "/auth/admin/users") {
    const [a, b] = await Promise.all([
      run(supabase.from("profiles").select("*")),
      run(supabase.from("user_roles").select("*")),
    ]);
    return ok(a.map((x) => ({ ...x, ...b.find((r) => r.user_id === x.id) })));
  }
  if (path === "/admin/partners" || path === "/admin/leadership") {
    const entryType = path.endsWith("partners") ? "partner" : "leadership";
    const rows = await run(
      supabase
        .from("directory_entries")
        .select("*")
        .eq("entry_type", entryType)
        .order("position"),
    );
    return ok(
      rows.map((x) => ({
        ...x.metadata,
        ...x,
        status: x.status === "archived" ? "hidden" : x.status,
        display_order: x.position,
      })),
    );
  }
  if ((m = path.match(/^\/cms\/(.+?)(?:\/([^/]+))?$/))) {
    let r = m[1],
      id = m[2];
    if (T[r]) {
      let x = await list(r, true, p);
      return ok(id ? x.find((v) => v.id === id) : x);
    }
    if (r === "media")
      return ok(
        await run(supabase.from("media").select("*").is("deleted_at", null)),
      );
    if (r === "categories" || r === "authors")
      return ok(
        await run(
          supabase
            .from(r === "authors" ? "cms_authors" : "categories")
            .select("*"),
        ),
      );
    if (r === "homepage") return ok([(await site()).homepage]);
    if (r === "navigation") return ok([(await site()).navigation]);
    if (r === "dashboard") return ok({ counts: {}, recent_activity: [] });
  }
  if (
    (m = path.match(
      /^\/(articles|blogs|news|pages|events|opportunities|initiatives|organizations)(?:\/([^/]+))?$/,
    ))
  ) {
    let x = await list(m[1], false, p);
    return ok(m[2] ? x.find((v) => v.slug === m[2] || v.id === m[2]) : x);
  }
  if (path === "/leadership" || path === "/partners")
    return ok(
      (
        await run(
          supabase
            .from("directory_entries")
            .select("*")
            .eq("entry_type", path === "/partners" ? "partner" : "leadership")
            .eq("status", "published")
            .order("position"),
        )
      ).map((x) => ({ ...x.metadata, ...x })),
    );
  bad("Unsupported operation: GET " + path, 404);
}
async function post(path, b = {}, c = {}) {
  let m;
  if (path === "/auth/login") {
    const { data, error } = await supabase.auth.signInWithPassword({
      email: b.email,
      password: b.password,
    });
    if (error) bad(error, 401);
    return ok({ user: await me(), access_token: data.session.access_token });
  }
  if (path === "/auth/register") {
    const { data, error } = await supabase.auth.signUp({
      email: b.email,
      password: b.password,
      options: { data: { first_name: b.first_name, last_name: b.last_name } },
    });
    if (error) bad(error);
    return ok({ user: data.user });
  }
  if (path === "/auth/logout") {
    await supabase.auth.signOut();
    return ok({ success: true });
  }
  if (path === "/auth/forgot-password") {
    await supabase.auth.resetPasswordForEmail(b.email, {
      redirectTo: location.origin + "/reset-password",
    });
    return ok({ success: true });
  }
  if (path === "/auth/reset-password") {
    await supabase.auth.updateUser({ password: b.password });
    return ok({ success: true });
  }
  if (path === "/auth/verify-email" || path === "/auth/resend-verification")
    return ok({ success: true });
  if (path === "/auth/admin/users") {
    const { data, error } = await supabase.functions.invoke("admin-users", {
      body: { action: "create", payload: b },
    });
    if (error) bad(error);
    return ok(data);
  }
  if (path === "/admin/partners" || path === "/admin/leadership") {
    const entryType = path.endsWith("partners") ? "partner" : "leadership";
    const legacyId = `${entryType}-${crypto.randomUUID()}`;
    return ok(
      await run(
        supabase
          .from("directory_entries")
          .insert({
            legacy_id: legacyId,
            entry_type: entryType,
            slug:
              b.slug ||
              (b.name || legacyId)
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, "-")
                .replace(/^-|-$/g, ""),
            name: b.name,
            title:
              entryType === "leadership"
                ? b.custom_role || b.role_title || ""
                : b.title || b.short_name || "",
            description: b.description || "",
            image_url: b.image_url || "",
            url:
              entryType === "leadership"
                ? b.linkedin_url || ""
                : b.url || b.website || "",
            position: Number(b.display_order ?? b.position ?? b.order ?? 1),
            status: b.status === "hidden" ? "archived" : b.status || "draft",
            metadata:
              entryType === "leadership"
                ? {
                    role_title: b.role_title || "",
                    custom_role: b.custom_role || "",
                    department: b.department || "",
                    country_region: b.country_region || "",
                    responsibilities: b.responsibilities || "",
                    linkedin_url: b.linkedin_url || "",
                    email: b.email || "",
                    website: b.website || "",
                    display_order: Number(b.display_order || 1),
                    featured: !!b.featured,
                  }
                : { short_name: b.short_name || "", featured: !!b.featured },
          })
          .select()
          .single(),
      ),
    );
  }
  if (path === "/newsletter/subscribe")
    return ok(
      await run(
        supabase.rpc("subscribe_newsletter", {
          p_email: b.email,
          p_first_name: b.first_name || "",
          p_country: b.country || "Unknown",
        }),
      ),
    );
  if (path === "/contact")
    return ok(
      await run(
        supabase.rpc("submit_contact_message", {
          p_name: b.name || "",
          p_email: b.email,
          p_message: b.message,
          p_subject: b.subject || "",
          p_category: b.category || "General Inquiry",
          p_organization: b.organization || "",
        }),
      ),
    );
  if (path === "/me/saved") {
    const u = await authUser(),
      old = await run(
        supabase
          .from("saved_content")
          .select("id")
          .eq("user_id", u.id)
          .eq("item_id", b.item_id),
      );
    if (old.length) {
      await run(supabase.from("saved_content").delete().eq("id", old[0].id));
      return ok({ saved: false });
    }
    await run(
      supabase.from("saved_content").insert({
        user_id: u.id,
        item_id: b.item_id,
        item_type: b.item_type,
        title: b.title,
        url: b.url,
      }),
    );
    return ok({ saved: true });
  }
  if ((m = path.match(/^\/engagement\/([0-9a-f-]+)\/view$/)))
    return ok(
      await run(
        supabase.rpc("record_content_view", {
          p_content_id: m[1],
          p_session_id: b.session_id || "anonymous",
        }),
      ),
    );
  if ((m = path.match(/^\/engagement\/([0-9a-f-]+)\/like$/))) {
    const u = await authUser();
    await run(
      supabase
        .from("content_likes")
        .upsert({ content_id: m[1], user_id: u.id }),
    );
    return ok({ liked: true });
  }
  if ((m = path.match(/^\/engagement\/([0-9a-f-]+)\/comments$/))) {
    const u = await authUser();
    return ok(
      await run(
        supabase
          .from("content_comments")
          .insert({
            content_id: m[1],
            author_id: u.id,
            body: b.body,
            status: "approved",
          })
          .select()
          .single(),
      ),
    );
  }
  if (path === "/cms/site-builder/publish") {
    await authUser();
    const settings = await run(
      supabase
        .from("site_settings")
        .select("*")
        .like("setting_key", "builder_%"),
    );
    for (const item of settings) {
      const value = item.value || {};
      await run(
        supabase
          .from("site_settings")
          .update({
            value: {
              ...value,
              live: value.draft || value.live || {},
              published_at: new Date().toISOString(),
            },
          })
          .eq("id", item.id),
      );
    }
    if (Array.isArray(b.page_ids) && b.page_ids.length)
      await run(
        supabase
          .from("content_items")
          .update({
            status: "published",
            published_at: new Date().toISOString(),
          })
          .in("id", b.page_ids)
          .eq("content_type", "page"),
      );
    return ok({
      success: true,
      published: settings.length + (b.page_ids?.length || 0),
    });
  }
  if ((m = path.match(/^\/cms\/heritage\/(communities|places|sources)$/)))
    return ok(await run(supabase.from(H[m[1]]).insert(b).select().single()));
  if (
    (m = path.match(
      /^\/cms\/(articles|blogs|news|pages|events|opportunities|initiatives|organizations)$/,
    ))
  )
    return ok(
      flat(
        await run(
          supabase.from("content_items").insert(row(m[1], b)).select().single(),
        ),
      ),
    );
  if ((m = path.match(/^\/cms\/(categories|authors)$/)))
    return ok(
      await run(
        supabase
          .from(m[1] === "authors" ? "cms_authors" : "categories")
          .insert(b)
          .select()
          .single(),
      ),
    );
  bad("Unsupported operation: POST " + path, 404);
}
async function put(path, b = {}) {
  let m;
  if (
    (m = path.match(
      /^\/cms\/site-builder\/settings\/(global_styles|header|footer|navigation)$/,
    ))
  ) {
    const u = await authUser();
    const current = await run(
      supabase
        .from("site_settings")
        .select("value")
        .eq("setting_key", `builder_${m[1]}`)
        .maybeSingle(),
    );
    const value = {
      ...(current?.value || {}),
      draft: b,
      updated_at: new Date().toISOString(),
      updated_by: u.id,
    };
    return ok(
      (
        await run(
          supabase
            .from("site_settings")
            .upsert(
              { setting_key: `builder_${m[1]}`, value },
              { onConflict: "setting_key" },
            )
            .select()
            .single(),
        )
      ).value,
    );
  }
  if ((m = path.match(/^\/cms\/site-builder\/restore\/([0-9a-f-]+)$/))) {
    const revision = await run(
      supabase.from("content_revisions").select("*").eq("id", m[1]).single(),
    );
    if (!revision.content_id) bad("Revision is not linked to a page");
    const snapshot = revision.snapshot || {};
    const restored = row("pages", {
      ...snapshot,
      status: "draft",
      metadata: { ...(snapshot.metadata || {}), restored_from: revision.id },
    });
    return ok(
      flat(
        await run(
          supabase
            .from("content_items")
            .update(restored)
            .eq("id", revision.content_id)
            .select()
            .single(),
        ),
      ),
    );
  }
  if (path === "/me/profile") {
    let u = await authUser(),
      x = { ...b };
    if (x.avatar) {
      x.avatar_url = x.avatar;
      delete x.avatar;
    }
    if (x.organization) {
      x.organisation = x.organization;
      delete x.organization;
    }
    return ok(
      await run(
        supabase.from("profiles").update(x).eq("id", u.id).select().single(),
      ),
    );
  }
  if ((m = path.match(/^\/admin\/(partners|leadership)\/([0-9a-f-]+)$/)))
    return ok(
      await run(
        supabase
          .from("directory_entries")
          .update({
            name: b.name,
            title:
              m[1] === "leadership"
                ? b.custom_role || b.role_title || ""
                : b.title || b.short_name || "",
            description: b.description || "",
            image_url: b.image_url || "",
            url:
              m[1] === "leadership"
                ? b.linkedin_url || ""
                : b.url || b.website || "",
            position: Number(b.display_order ?? b.position ?? b.order ?? 1),
            status: b.status === "hidden" ? "archived" : b.status || "draft",
            metadata:
              m[1] === "leadership"
                ? {
                    ...(b.metadata || {}),
                    role_title: b.role_title || "",
                    custom_role: b.custom_role || "",
                    department: b.department || "",
                    country_region: b.country_region || "",
                    responsibilities: b.responsibilities || "",
                    linkedin_url: b.linkedin_url || "",
                    email: b.email || "",
                    website: b.website || "",
                    display_order: Number(b.display_order || 1),
                    featured: !!b.featured,
                  }
                : {
                    ...(b.metadata || {}),
                    short_name: b.short_name || "",
                    featured: !!b.featured,
                  },
          })
          .eq("id", m[2])
          .eq("entry_type", m[1] === "partners" ? "partner" : "leadership")
          .select()
          .single(),
      ),
    );
  if ((m = path.match(/^\/auth\/admin\/users\/([0-9a-f-]+)$/))) {
    const { data, error } = await supabase.functions.invoke("admin-users", {
      body: { action: "update", user_id: m[1], payload: b },
    });
    if (error) bad(error);
    return ok(data);
  }
  if (
    (m = path.match(
      /^\/cms\/heritage\/(communities|places|sources)\/([0-9a-f-]+)$/,
    ))
  )
    return ok(
      await run(
        supabase.from(H[m[1]]).update(b).eq("id", m[2]).select().single(),
      ),
    );
  if (
    (m = path.match(
      /^\/cms\/(articles|blogs|news|pages|events|opportunities|initiatives|organizations)\/([0-9a-f-]+)$/,
    ))
  )
    return ok(
      flat(
        await run(
          supabase
            .from("content_items")
            .update(row(m[1], b))
            .eq("id", m[2])
            .select()
            .single(),
        ),
      ),
    );
  if ((m = path.match(/^\/cms\/media\/([0-9a-f-]+)$/)))
    return ok(
      await run(
        supabase
          .from("media")
          .update({
            name: b.name,
            alt_text: b.alt_text,
            caption: b.caption,
            status: b.status,
          })
          .eq("id", m[1])
          .select()
          .single(),
      ),
    );
  if ((m = path.match(/^\/cms\/(categories|authors)\/([0-9a-f-]+)$/)))
    return ok(
      await run(
        supabase
          .from(m[1] === "authors" ? "cms_authors" : "categories")
          .update(b)
          .eq("id", m[2])
          .select()
          .single(),
      ),
    );
  if (path === "/cms/engagement/settings")
    return ok(
      await run(
        supabase
          .from("engagement_settings")
          .update(b)
          .eq("id", "default")
          .select()
          .single(),
      ),
    );
  if ((m = path.match(/^\/cms\/engagement\/comments\/([0-9a-f-]+)$/)))
    return ok(
      await run(
        supabase
          .from("content_comments")
          .update({ status: b.status })
          .eq("id", m[1])
          .select()
          .single(),
      ),
    );
  if ((m = path.match(/^\/engagement\/[0-9a-f-]+\/comments\/([0-9a-f-]+)$/)))
    return ok(
      await run(
        supabase
          .from("content_comments")
          .update({ body: b.body, edited_at: new Date().toISOString() })
          .eq("id", m[1])
          .select()
          .single(),
      ),
    );
  if (path === "/admin/site-settings") {
    await run(
      supabase
        .from("site_settings")
        .upsert(
          { setting_key: "site", value: b },
          { onConflict: "setting_key" },
        ),
    );
    return ok(b);
  }
  bad("Unsupported operation: PUT " + path, 404);
}
async function del(path) {
  let m;
  if ((m = path.match(/^\/admin\/(partners|leadership)\/([0-9a-f-]+)$/))) {
    await run(
      supabase
        .from("directory_entries")
        .delete()
        .eq("id", m[2])
        .eq("entry_type", m[1] === "partners" ? "partner" : "leadership"),
    );
    return ok({ success: true });
  }
  if ((m = path.match(/^\/engagement\/([0-9a-f-]+)\/like$/))) {
    const u = await authUser();
    await run(
      supabase
        .from("content_likes")
        .delete()
        .eq("content_id", m[1])
        .eq("user_id", u.id),
    );
    return ok({ liked: false });
  }
  if ((m = path.match(/^\/engagement\/[0-9a-f-]+\/comments\/([0-9a-f-]+)$/))) {
    await run(
      supabase
        .from("content_comments")
        .update({ status: "deleted", body: "Comment removed" })
        .eq("id", m[1]),
    );
    return ok({ success: true });
  }
  if (
    (m = path.match(
      /^\/cms\/(articles|blogs|news|pages|events|opportunities|initiatives|organizations)\/([0-9a-f-]+)$/,
    ))
  ) {
    await run(
      supabase
        .from("content_items")
        .update({ status: "archived", deleted_at: new Date().toISOString() })
        .eq("id", m[2]),
    );
    return ok({ success: true });
  }
  if ((m = path.match(/^\/cms\/media\/([0-9a-f-]+)$/))) {
    await run(
      supabase
        .from("media")
        .update({ status: "archived", deleted_at: new Date().toISOString() })
        .eq("id", m[1]),
    );
    return ok({ success: true });
  }
  bad("Unsupported operation: DELETE " + path, 404);
}
async function postWithMedia(path, b, c = {}) {
  if (path !== "/cms/media/upload/file") return post(path, b, c);
  const f = b.get("file"),
    folder = b.get("folder") || "media",
    object_path =
      folder +
      "/" +
      crypto.randomUUID() +
      "-" +
      f.name.replace(/[^a-zA-Z0-9._-]/g, "-");
  c.onUploadProgress?.({ loaded: 1, total: 2 });
  await run(
    supabase.storage
      .from("public-media")
      .upload(object_path, f, { contentType: f.type }),
  );
  const public_url = supabase.storage
      .from("public-media")
      .getPublicUrl(object_path).data.publicUrl,
    saved = await run(
      supabase
        .from("media")
        .insert({
          name: f.name.replace(/\.[^.]+$/, ""),
          filename: f.name,
          bucket: "public-media",
          object_path,
          public_url,
          media_type: f.type.startsWith("video/") ? "video" : "image",
          mime_type: f.type,
          size_bytes: f.size,
          alt_text: b.get("alt_text") || "",
          caption: b.get("caption") || "",
          status: "published",
        })
        .select()
        .single(),
    );
  c.onUploadProgress?.({ loaded: 2, total: 2 });
  return ok(saved);
}
async function putExtended(path, b) {
  if (path.startsWith("/cms/homepage/")) {
    for (const [position, s] of (b.sections || []).entries())
      await run(
        supabase.from("homepage_sections").upsert(
          {
            section_key: s.id || "section-" + position,
            section_type: s.type || s.id,
            title: s.title || "",
            position,
            visible: s.visible !== false,
            status: "published",
            settings: s,
          },
          { onConflict: "section_key" },
        ),
      );
    return ok((await site()).homepage);
  }
  if (path.startsWith("/cms/navigation/")) {
    for (const [position, x] of (b.items || []).entries())
      await run(
        supabase
          .from("navigation_items")
          .upsert(
            { ...x, menu_key: "primary", position },
            { onConflict: "id" },
          ),
      );
    return ok((await site()).navigation);
  }
  return put(path, b);
}
async function postMember(path, b = {}, c = {}) {
  if (path === "/auth/register") {
    const { data, error } = await supabase.auth.signUp({
      email: b.email,
      password: b.password,
      options: {
        data: {
          first_name: b.first_name,
          last_name: b.last_name,
          country: b.country || "",
          country_code: b.country_code || "",
          city: b.city || "",
        },
      },
    });
    if (error) bad(error);
    if (data.session && data.user)
      await run(
        supabase.from("profiles").update(profileRow(b)).eq("id", data.user.id),
      );
    return ok({ user: data.user });
  }
  if (path === "/me/avatar") {
    const u = await authUser(),
      f = b.get("file");
    if (
      !f ||
      !["image/jpeg", "image/png", "image/webp"].includes(f.type) ||
      f.size > 5242880
    )
      bad("Choose a JPEG, PNG or WebP image up to 5 MB.");
    const ext = (f.name.split(".").pop() || "jpg").toLowerCase(),
      object_path = `${u.id}/profile.${ext}`;
    await run(
      supabase.storage.from("avatars").upload(object_path, f, {
        contentType: f.type,
        upsert: true,
        cacheControl: "3600",
      }),
    );
    const public_url =
      supabase.storage.from("avatars").getPublicUrl(object_path).data
        .publicUrl + `?v=${Date.now()}`;
    await run(
      supabase
        .from("profiles")
        .update({ avatar_url: public_url })
        .eq("id", u.id),
    );
    return ok({ avatar_url: public_url, object_path });
  }
  if (path === "/me/connections") {
    const u = await authUser();
    if (!b.recipient_id || b.recipient_id === u.id)
      bad("You cannot connect with yourself.");
    const { data, error } = await supabase.rpc("request_member_connection", {
      p_recipient_id: b.recipient_id,
    });
    if (error) {
      if (error.code === "23505")
        return ok({
          state: "pending_sent",
          message: "Connection request already sent.",
        });
      bad(
        ["22023", "P0002"].includes(error.code)
          ? error.message
          : "Could not send connection request. Please try again.",
      );
    }
    return ok(data);
  }
  return postWithMedia(path, b, c);
}
async function putMember(path, b = {}) {
  if (path === "/me/profile") {
    const u = await authUser();
    return ok(
      await run(
        supabase
          .from("profiles")
          .update(profileRow(b))
          .eq("id", u.id)
          .select()
          .single(),
      ),
    );
  }
  if (path === "/me/account") {
    const { data, error } = await supabase.auth.updateUser(
      b.password ? { password: b.password } : {},
    );
    if (error) bad(error);
    return ok(data.user);
  }
  if (path === "/me/notifications/read-all") {
    const u = await authUser();
    await run(
      supabase
        .from("notifications")
        .update({ read_at: new Date().toISOString() })
        .eq("user_id", u.id)
        .is("read_at", null),
    );
    return ok({ success: true });
  }
  let m;
  if ((m = path.match(/^\/me\/notifications\/([0-9a-f-]+)$/))) {
    const u = await authUser();
    return ok(
      await run(
        supabase
          .from("notifications")
          .update({ read_at: new Date().toISOString() })
          .eq("id", m[1])
          .eq("user_id", u.id)
          .select()
          .single(),
      ),
    );
  }
  if ((m = path.match(/^\/me\/connections\/([0-9a-f-]+)$/))) {
    await authUser();
    const action =
      b.action ||
      { accepted: "accept", declined: "decline", cancelled: "cancel" }[
        b.status
      ];
    const { data, error } = await supabase.rpc("manage_member_connection", {
      p_connection_id: m[1],
      p_action: action,
    });
    if (error)
      bad(
        error.code === "42501" || error.code === "22023"
          ? error.message
          : "Could not update this connection. Please try again.",
      );
    return ok(data);
  }
  return putExtended(path, b);
}
const api = {
  get,
  post: postMember,
  put: putMember,
  delete: del,
  patch: putMember,
  interceptors: { request: { use: () => {} } },
};
export const API = "";
export function formatApiErrorDetail(x) {
  return typeof x === "string"
    ? x
    : x?.msg || "Something went wrong. Please try again.";
}
export default api;
